import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../types';
import { Bot, User } from 'lucide-react';

interface Props {
    messages: ChatMessage[];
    isLoading?: boolean;
}

export default function ChatWindow({ messages, isLoading }: Props) {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    return (
        <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-navy-50 dark:bg-navy-950">
            {messages.map((message) => (
                <div
                    key={message.id}
                    className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'
                        }`}
                >
                    {message.role === 'assistant' && (
                        <div className="w-10 h-10 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center flex-shrink-0 border border-accent-200 dark:border-accent-800">
                            <Bot className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                        </div>
                    )}

                    <div
                        className={`max-w-[80%] rounded-2xl px-5 py-3 ${message.role === 'user'
                            ? 'bg-navy-700 dark:bg-navy-600 text-white'
                            : 'bg-white dark:bg-navy-800 border border-navy-200 dark:border-navy-700 text-navy-900 dark:text-navy-100 shadow-sm'
                            }`}
                    >
                        {message.role === 'assistant' ? (
                            <div className="prose dark:prose-invert max-w-none text-sm">
                                <ReactMarkdown>{message.content}</ReactMarkdown>
                            </div>
                        ) : (
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        )}
                    </div>

                    {message.role === 'user' && (
                        <div className="w-10 h-10 rounded-full bg-navy-200 dark:bg-navy-700 flex items-center justify-center flex-shrink-0 border border-navy-300 dark:border-navy-600">
                            <User className="w-5 h-5 text-navy-600 dark:text-navy-300" />
                        </div>
                    )}
                </div>
            ))}

            {isLoading && (
                <div className="flex gap-4 justify-start">
                    <div className="w-10 h-10 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center flex-shrink-0 border border-accent-200 dark:border-accent-800">
                        <Bot className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                    </div>
                    <div className="bg-white dark:bg-navy-800 border border-navy-200 dark:border-navy-700 rounded-2xl px-5 py-4 shadow-sm">
                        <div className="flex space-x-2">
                            <div className="w-2 h-2 bg-accent-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-accent-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-accent-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
}
